/**
 * Zeichnung des bewegten Titelbereichs (Hoehenlinien, Gebiet als Modell, Orte, Messpunkte) ohne
 * React: gemeinsam fuer HeroAnimation (Portal) und login-hero (Keycloak-Anmeldeseite).
 */
import type { HeroScene } from './hero-scene.js';

const ease = (x: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3);

/** `tile`: kleine Kachel, Gebiet rechts in voller Staerke, ohne Namen (die Kachel nennt sie selbst). */
export function drawHero(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, scene: HeroScene, hue: number, chroma: number, tile = false): void {
  const c = Math.min(0.16, Math.max(0.05, chroma));
  const line = (a: number) => `oklch(0.86 ${c * 0.7} ${hue} / ${a})`;
  ctx.clearRect(0, 0, w, h);
  contours(ctx, w, h, t, line);
  if (scene.rings.length > 0) model(ctx, w, h, t, scene, hue, c, line, tile);
}

/** Hoehenlinien eines langsam wandernden Feldes (Marching Squares). */
function contours(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, line: (a: number) => string): void {
  const cell = w < 700 ? 18 : 14;
  const cols = Math.ceil(w / cell) + 1;
  const rows = Math.ceil(h / cell) + 1;
  const s = 0.0042;
  const vals = new Float32Array(cols * rows);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const px = x * cell;
      const py = y * cell;
      vals[y * cols + x] =
        Math.sin(px * s + t * 0.11) * 0.9 +
        Math.cos(py * s * 1.3 - t * 0.08) * 0.8 +
        Math.sin((px + py) * s * 0.7 + t * 0.05) * 0.7 +
        Math.cos(Math.hypot(px - w * 0.72, py - h * 0.55) * s * 1.6 - t * 0.14) * 1.1;
    }
  }
  const levels = [-1.6, -0.9, -0.3, 0.3, 0.9, 1.5, 2.1];
  ctx.lineWidth = 1;
  levels.forEach((lv, li) => {
    ctx.strokeStyle = li === 3 ? line(0.3) : line(0.13);
    ctx.beginPath();
    for (let y = 0; y < rows - 1; y++) {
      for (let x = 0; x < cols - 1; x++) {
        const a = vals[y * cols + x];
        const b = vals[y * cols + x + 1];
        const cc = vals[(y + 1) * cols + x + 1];
        const d = vals[(y + 1) * cols + x];
        const idx = (a > lv ? 8 : 0) | (b > lv ? 4 : 0) | (cc > lv ? 2 : 0) | (d > lv ? 1 : 0);
        if (idx === 0 || idx === 15) continue;
        const px = x * cell;
        const py = y * cell;
        const top: [number, number] = [px + (cell * (lv - a)) / (b - a), py];
        const right: [number, number] = [px + cell, py + (cell * (lv - b)) / (cc - b)];
        const bottom: [number, number] = [px + (cell * (lv - d)) / (cc - d), py + cell];
        const left: [number, number] = [px, py + (cell * (lv - a)) / (d - a)];
        const segs = SEGMENTS[idx](top, right, bottom, left);
        for (let i = 0; i < segs.length; i += 2) {
          ctx.moveTo(segs[i][0], segs[i][1]);
          ctx.lineTo(segs[i + 1][0], segs[i + 1][1]);
        }
      }
    }
    ctx.stroke();
  });
}

type P = [number, number];
const SEGMENTS: Record<number, (t: P, r: P, b: P, l: P) => P[]> = {
  1: (_t, _r, b, l) => [l, b],
  14: (_t, _r, b, l) => [l, b],
  2: (_t, r, b) => [b, r],
  13: (_t, r, b) => [b, r],
  3: (_t, r, _b, l) => [l, r],
  12: (_t, r, _b, l) => [l, r],
  4: (t, r) => [t, r],
  11: (t, r) => [t, r],
  5: (t, r, b, l) => [l, t, b, r],
  6: (t, _r, b) => [t, b],
  9: (t, _r, b) => [t, b],
  7: (t, _r, _b, l) => [l, t],
  8: (t, _r, _b, l) => [l, t],
  10: (t, r, b, l) => [l, b, t, r],
};

/** Gebiet isometrisch: breit rechts neben dem Text, schmal mittig hinter dem Text und blasser. */
function model(ctx: CanvasRenderingContext2D, w: number, h: number, t: number, scene: HeroScene, hue: number, c: number, line: (a: number) => string, tile: boolean): void {
  const wide = w >= 900 || tile;
  const box = tile ? { x: w * 0.42, y: h * 0.12, w: w * 0.52, h: h * 0.74 } : wide ? { x: w * 0.5, y: h * 0.22, w: w * 0.42, h: h * 0.62 } : { x: w * 0.08, y: h * 0.1, w: w * 0.84, h: h * 0.32 };
  const pts = scene.rings.flat();
  const X = (u: number, v: number) => (u - v) * 0.87;
  const Y = (u: number, v: number) => (u + v) * 0.5;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [u, v] of pts) {
    minX = Math.min(minX, X(u, v));
    maxX = Math.max(maxX, X(u, v));
    minY = Math.min(minY, Y(u, v));
    maxY = Math.max(maxY, Y(u, v));
  }
  const S = Math.min(box.w / Math.max(maxX - minX, 1e-6), box.h / Math.max(maxY - minY, 1e-6));
  const ox = box.x + (box.w - (maxX - minX) * S) / 2 - minX * S;
  const oy = box.y + (box.h - (maxY - minY) * S) / 2 - minY * S + Math.sin(t * 0.35) * 3;
  const iso = (u: number, v: number, z = 0): P => [ox + X(u, v) * S, oy + Y(u, v) * S - z * S];

  ctx.save();
  if (!wide) ctx.globalAlpha = 0.35;

  // Flaeche und Raster (Flurstuecke) innerhalb der Grenzen.
  const reveal = ease(t / 2.4);
  ctx.beginPath();
  for (const r of scene.rings) {
    r.forEach(([u, v], i) => {
      const [x, y] = iso(u, v);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
  }
  ctx.fillStyle = `oklch(0.55 ${c} ${hue} / ${0.14 * reveal})`;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = line(0.1 * reveal);
  ctx.lineWidth = 1;
  ctx.beginPath();
  const step = 0.025;
  for (let g = -0.5; g <= 1.5; g += step) {
    const [x1, y1] = iso(g, -0.5);
    const [x2, y2] = iso(g, 1.5);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    const [x3, y3] = iso(-0.5, g);
    const [x4, y4] = iso(1.5, g);
    ctx.moveTo(x3, y3);
    ctx.lineTo(x4, y4);
  }
  ctx.stroke();
  // Lichtband, das langsam ueber das Gebiet zieht.
  const sweep = ((t * 0.08) % 1.6) - 0.3;
  const [sx1, sy1] = iso(sweep - 0.15, -0.2);
  const [sx2, sy2] = iso(sweep + 0.15, -0.2);
  const grad = ctx.createLinearGradient(sx1, sy1, sx2, sy2);
  grad.addColorStop(0, line(0));
  grad.addColorStop(0.5, line(0.12));
  grad.addColorStop(1, line(0));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  // Grenzen zeichnen sich ein; Schein als breite, blasse Linie darunter (shadowBlur waere zu teuer).
  for (const [width, alpha] of [
    [6, 0.12],
    [1.5, 0.8],
  ] as [number, number][]) {
    ctx.lineWidth = width;
    ctx.strokeStyle = line(alpha);
    ctx.beginPath();
    for (const r of scene.rings) {
      const n = Math.max(2, Math.floor(r.length * reveal));
      for (let i = 0; i < n; i++) {
        const [x, y] = iso(r[i][0], r[i][1]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      if (reveal >= 1) ctx.closePath();
    }
    ctx.stroke();
  }

  // Orte wachsen aus dem Plan, von hinten nach vorn gezeichnet.
  const order = [...scene.buildings].sort((a, b) => a.u + a.v - (b.u + b.v));
  for (const b of order) {
    const grow = ease((t - b.delay) / 1.6);
    if (grow <= 0) continue;
    const z = b.size * b.height * grow;
    const s = b.size;
    const B = iso(b.u + s, b.v);
    const C = iso(b.u + s, b.v + s);
    const D = iso(b.u, b.v + s);
    const A2 = iso(b.u, b.v, z);
    const B2 = iso(b.u + s, b.v, z);
    const C2 = iso(b.u + s, b.v + s, z);
    const D2 = iso(b.u, b.v + s, z);
    ctx.fillStyle = `oklch(0.24 ${c * 0.5} ${hue} / 0.9)`;
    poly(ctx, [B, C, D, D2, A2, B2]);
    ctx.fill();
    ctx.fillStyle = `oklch(0.8 ${c} ${hue} / 0.22)`;
    poly(ctx, [A2, B2, C2, D2]);
    ctx.fill();
    ctx.strokeStyle = line(0.3 + 0.4 * grow);
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(A2[0], A2[1]);
    for (const p of [B2, C2, D2, A2]) ctx.lineTo(p[0], p[1]);
    for (const [p, q] of [
      [B, B2],
      [C, C2],
      [D, D2],
    ] as [P, P][]) {
      ctx.moveTo(p[0], p[1]);
      ctx.lineTo(q[0], q[1]);
    }
    ctx.stroke();
  }

  // Datenwege zwischen den Messpunkten, darauf laufende Punkte.
  const amber = (a: number) => `rgba(240,160,85,${a})`;
  const pinH = 0.09;
  const tops = scene.pins.map((p) => iso(p.u, p.v, pinH));
  const show = ease((t - 2.2) / 1.2);
  if (show > 0 && tops.length > 1) {
    ctx.setLineDash([3, 5]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = amber(0.35 * show);
    for (let i = 0; i < tops.length; i++) {
      const a = tops[i];
      const b = tops[(i + 1) % tops.length];
      if (tops.length === 2 && i === 1) break;
      const mx = (a[0] + b[0]) / 2;
      const my = Math.min(a[1], b[1]) - Math.hypot(b[0] - a[0], b[1] - a[1]) * 0.25;
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.quadraticCurveTo(mx, my, b[0], b[1]);
      ctx.stroke();
      const f = (t * 0.25 + i * 0.37) % 1;
      const qx = (1 - f) * (1 - f) * a[0] + 2 * (1 - f) * f * mx + f * f * b[0];
      const qy = (1 - f) * (1 - f) * a[1] + 2 * (1 - f) * f * my + f * f * b[1];
      ctx.fillStyle = amber(0.9 * show);
      ctx.beginPath();
      ctx.arc(qx, qy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.setLineDash([]);
  }

  // Messpunkte mit dem Namen der Gemeinde.
  ctx.font = `600 ${wide ? 13 : 11}px ui-sans-serif, system-ui, sans-serif`;
  scene.pins.forEach((p, i) => {
    const appear = ease((t - 1.8 - i * 0.25) / 0.8);
    if (appear <= 0) return;
    const base = iso(p.u, p.v);
    const top = tops[i];
    const pulse = (Math.sin(t * 2 + i * 0.9) + 1) / 2;
    ctx.strokeStyle = amber(0.6 * appear);
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.moveTo(base[0], base[1]);
    ctx.lineTo(top[0], top[1] + (1 - appear) * pinH * S);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = amber((0.12 + 0.2 * pulse) * appear);
    ctx.beginPath();
    ctx.arc(top[0], top[1], 7 + 8 * pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = amber(appear);
    ctx.beginPath();
    ctx.arc(top[0], top[1], 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(255,255,255,${0.85 * appear})`;
    if (!tile) ctx.fillText(p.name, top[0] + 12, top[1] + 4);
  });
  ctx.restore();
}

function poly(ctx: CanvasRenderingContext2D, ps: P[]): void {
  ctx.beginPath();
  ctx.moveTo(ps[0][0], ps[0][1]);
  for (const p of ps.slice(1)) ctx.lineTo(p[0], p[1]);
  ctx.closePath();
}
